import React, {memo} from 'react';

import {floatPriceFromFormatted} from '@optimaxdev/utils';
import {connect} from 'react-redux';

import {BaseItemPrice} from 'desktop/components/baseItemPrice/baseItemPrice';
import {PLA} from 'desktop/pages/checkout/constants/checkout';
import {
    getCurrentRebateDiscount,
    getNextLvlRebateDiscount,
} from 'desktop/pages/checkout/store/selectors/orderReviewTotals/utils/rebateDiscount';
import {ApplicationStoreType} from 'desktop/store';
import {getPreparedCLPrescription} from 'desktop/utils/getPreparedCLPrescription/getPreparedCLPrescription';

import {CLSubscriptionFeatureConnected} from '../../features/cLSubscription/components/clSubscriptionFeature/cLSubscriptionFeature';
import {PrescriptionType} from '../../store/fixtures/prescriptionFixture';
import {getIsUpdateItemPending} from '../../store/selectors';
import {RebateDiscountType} from '../../types';
import {ContactLensesNameType} from '../../types/quoteItem';
import {BaseInfoRow} from '../baseInfoRowV2/baseInfoRow';
import {CartItemBoxCount} from '../cartItemBoxCount/cartItemBoxCount';
import {RebateWidget} from '../rebateWidget/rebateWidget';

import {PrescriptionViewTooltip} from './prescriptionViewTooltip/prescriptionViewTooltip';

import s from './cartItemContactsInfo.scss';

type PropsType = {
    id: string;
    name?: ContactLensesNameType;
    specialPrice?: string;
    price: string;
    lensType?: string;
    discountedPrice: number;
    isDisabled?: boolean;
    contactLensPerBox: string | null;
    prescription: PrescriptionType;
    subscription: {
        isSubscriptionAvailable: boolean;
        subscriptionMode: boolean;
        currentSubscriptionPlan: string;
        relatedProductSkuPrice?: string;
    } | null;
    rebateDiscount?: RebateDiscountType[];
    sku: string;
    prescriptionType?: number;
};

/**
 * Contact lenses LensType and Package information
 * @constructor
 */
export const CartItemContactsInfo: React.FC<PropsType> = memo(
    ({
        contactLensPerBox,
        discountedPrice,
        id,
        isDisabled,
        lensType,
        prescription,
        prescriptionType,
        price,
        rebateDiscount,
        sku,
        specialPrice,
        subscription,
    }) => {
        const color = prescription.od.color || prescription.os.color;
        const qty = Number(prescription.od.boxes) + Number(prescription.os.boxes);

        const nextRebateDiscount = getNextLvlRebateDiscount(rebateDiscount, qty);

        const preparedTooltipPrescriptionForEye = {
            od: getPreparedCLPrescription(prescription, prescriptionType, 'od'),
            os: getPreparedCLPrescription(prescription, prescriptionType, 'os'),
        };

        return (
            <>
                <div className={s.row}>
                    {lensType}, {contactLensPerBox} Pack <div className={s.divider} />{' '}
                    <PrescriptionViewTooltip
                        link={prescription.link}
                        method={prescription.method}
                        name={prescription.name as ContactLensesNameType}
                        od={preparedTooltipPrescriptionForEye.od}
                        os={preparedTooltipPrescriptionForEye.os}
                    />
                </div>
                <BaseInfoRow
                    title={
                        <BaseItemPrice
                            specialPrice={floatPriceFromFormatted(
                                String(discountedPrice || specialPrice),
                            )}
                            price={floatPriceFromFormatted(String(price))}
                        />
                    }
                    label="Price per box:"
                />
                {color && <BaseInfoRow label="Lens Color:" title={color} />}
                <BaseInfoRow
                    title={
                        <CLSubscriptionFeatureConnected
                            fallback={
                                <CartItemBoxCount
                                    id={id}
                                    isDisabled={isDisabled}
                                    prescription={prescription}
                                />
                            }
                            isSubscriptionAvailable={subscription?.isSubscriptionAvailable}
                        >
                            <CartItemBoxCount
                                id={id}
                                isDisabled={subscription?.subscriptionMode || isDisabled}
                                prescription={prescription}
                            />
                        </CLSubscriptionFeatureConnected>
                    }
                    label="No. of boxes:"
                />

                <RebateWidget
                    className={s.rebate}
                    containerClassName={s.rebateContainer}
                    currentDiscount={getCurrentRebateDiscount(rebateDiscount, qty)}
                    eventName="cartItem"
                    isContactLensesPla={sku.match(PLA) !== null}
                    nextDiscount={nextRebateDiscount?.discount}
                    qtyToDiscount={nextRebateDiscount?.qtyToDiscount}
                />
            </>
        );
    },
);

CartItemContactsInfo.displayName = 'CartItemContactsInfo';

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isDisabled: getIsUpdateItemPending(store),
});

export const CartItemContactsInfoConnect = connect(mapStateToProps)(CartItemContactsInfo);
